import {
  DrawerDescription,
  PopoverContent,
  PopoverTrigger,
  DrawerHeader,
  DrawerClose,
  DrawerTitle,
  FormSelect,
  FormSwitch,
  Typography,
  FormInput,
  Separator,
  Popover,
  Button,
  Badge,
  Label,
  Form,
} from '@ui/components';
import { PhoneCodes, Masks } from '@helpers/constant';
import { Cancel, Info } from '@ui/components/icons';
import { HasPermission } from '@shared/components';
import { useHasPermission } from '@shared/hooks';
import { getRoleColor } from '@shared/utils';
import { Role } from '@interfaces/models';
import { FC } from 'react';

import { useCurrentUserEdit } from './use-current-user-edit';

interface CurrentUserEditProps {
  onSuccess: () => void;
  onTransferPermissions?: () => void;
}

export const CurrentUserEdit: FC<CurrentUserEditProps> = ({
  onTransferPermissions = () => {},
  onSuccess,
}) => {
  const { onSubmit, isSubmitDisabled, isUpdatePending, form, t } = useCurrentUserEdit(onSuccess);
  const hasEditPermission = useHasPermission('USER_ACCOUNT_EDIT', 'WRITE');

  const role = form.watch('role');
  const email = form.watch('email');

  return (
    <>
      <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
        <DrawerTitle>{t('CURRENT_USER_EDIT.MANAGE_USER_ACCOUNT')}</DrawerTitle>
        <DrawerDescription />
        <DrawerClose asChild>
          <Button variant="ghost" size="small" icon>
            <Cancel />
          </Button>
        </DrawerClose>
      </DrawerHeader>
      <form
        className="flex-1 overflow-auto flex flex-col p-4 pt-5 gap-2"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex-1">
          <div className="space-y-5 pb-4">
            <Form {...form}>
              <div className="grid grid-cols-6 gap-2 items-center">
                <Typography className="col-span-6 mb-2" variant="labelMediumBold">
                  {t('CURRENT_USER_EDIT.USER_DETAILS')}
                </Typography>

                <Label className="col-span-2" htmlFor="firstName">
                  <Typography variant="labelSmallBook" className="text-weak" element="label">
                    {t('CURRENT_USER_EDIT.FIRST_NAME')}
                  </Typography>
                </Label>
                <FormInput
                  componentProps={{
                    id: 'firstName',
                  }}
                  placeholder={t('CURRENT_USER_EDIT.FIRST_NAME')}
                  className="w-full col-span-4"
                  disabled={!hasEditPermission}
                  formControl={form.control}
                  name="firstName"
                  size="small"
                  required
                />

                <Label className="col-span-2" htmlFor="lastName">
                  <Typography variant="labelSmallBook" className="text-weak" element="label">
                    {t('CURRENT_USER_EDIT.LAST_NAME')}
                  </Typography>
                </Label>
                <FormInput
                  componentProps={{
                    id: 'lastName',
                  }}
                  placeholder={t('CURRENT_USER_EDIT.LAST_NAME')}
                  className="w-full col-span-4"
                  disabled={!hasEditPermission}
                  formControl={form.control}
                  name="lastName"
                  size="small"
                  required
                />

                <Label className="col-span-2">
                  <Typography variant="labelSmallBook" className="text-weak" element="label">
                    {t('CURRENT_USER_EDIT.EMAIL')}
                  </Typography>
                </Label>
                <div className="flex items-center gap-4 justify-between  col-span-4">
                  <Typography variant="labelMediumBook" className="text-disabled" element="span">
                    {email}
                  </Typography>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" type="button" size="small" icon>
                        <Info />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="max-w-47" align="end">
                      <Typography className="text-paragraph" variant="labelMediumBook">
                        {t('CURRENT_USER_EDIT.EMAIL_CHANGE_TOOLTIP')}
                      </Typography>
                    </PopoverContent>
                  </Popover>
                </div>

                <Label className="col-span-2" htmlFor="phoneNumber">
                  <Typography variant="labelSmallBook" className="text-weak" element="label">
                    {t('CURRENT_USER_EDIT.PHONE_NUMBER')}
                  </Typography>
                </Label>
                <div className="grid grid-cols-4 gap-2 col-span-4">
                  <FormSelect
                    componentProps={{
                      autoComplete: 'phoneExt',
                    }}
                    disabled={!hasEditPermission}
                    formControl={form.control}
                    className="col-span-1"
                    options={PhoneCodes}
                    name="phoneExt"
                    size="small"
                  />
                  <FormInput
                    componentProps={{
                      maskOptions: { mask: Masks.PHONE },
                      autoComplete: 'phoneNumber',
                      id: 'phoneNumber',
                    }}
                    placeholder={t('CURRENT_USER_EDIT.PHONE_NUMBER')}
                    className="w-full col-span-3"
                    disabled={!hasEditPermission}
                    formControl={form.control}
                    name="phoneNumber"
                    size="small"
                  />
                </div>

                <Label className="col-span-2">
                  <Typography variant="labelSmallBook" className="text-weak" element="label">
                    {t('CURRENT_USER_EDIT.ROLE')}
                  </Typography>
                </Label>
                <div className="flex items-center gap-4 justify-between col-span-4">
                  {role && (
                    <Badge variant={getRoleColor(role as Role)} size="large">
                      {t(`AUTH.ROLE.${role}`)}
                    </Badge>
                  )}

                  <HasPermission
                    permissionFeature="COMPANY_ACCOUNT_DELEGATE_PERMISSIONS"
                    permissionType="WRITE"
                  >
                    <Button onClick={onTransferPermissions} variant="tertiary" type="button">
                      {t('CURRENT_USER_EDIT.MOVE_ROLE')}
                    </Button>
                  </HasPermission>
                </div>

                <Separator className="col-span-6 my-4" />

                <Typography className="col-span-6 mb-2" variant="labelMediumBold">
                  {t('CURRENT_USER_EDIT.MANAGE_MARKETING_CONSENTS')}
                </Typography>

                <div className="flex items-center justify-between gap-2 col-span-6 mb-2">
                  <Typography variant="labelMediumBook" element="label">
                    {t('MARKETING_CONSENTS.KEEP_PHONE_NUMBER_AGREEMENT')}
                  </Typography>
                  <FormSwitch
                    name="marketingPhoneManagementAccepted"
                    disabled={!hasEditPermission}
                    formControl={form.control}
                  />
                </div>

                <div className="flex items-center justify-between gap-2 col-span-6 mb-2">
                  <Typography variant="labelMediumBook" element="label">
                    {t('MARKETING_CONSENTS.KEEP_EMAIL_ADDRESS_AGREEMENT')}
                  </Typography>
                  <FormSwitch
                    name="marketingEmailManagementAccepted"
                    disabled={!hasEditPermission}
                    formControl={form.control}
                  />
                </div>
              </div>
            </Form>
          </div>
        </div>
        <Button
          disabled={isSubmitDisabled || !hasEditPermission}
          isLoading={isUpdatePending}
          className="shrink-0"
          type="submit"
          size="large"
          wide
        >
          {t('CURRENT_USER_EDIT.SAVE_CHANGES')}
        </Button>
      </form>
    </>
  );
};
